import { useEffect, useRef, useState } from "react";
import { WandSparklesIcon } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { ComingSoonBadge } from "./coming-soon-badge";

/**
 * Deriving a matrix from one question needs a backend route that does not exist
 * yet, so the field ships visible but disabled. Enabling it needs that call
 * behind the button; storing the question already works through `updateMatrix`.
 */
const QUESTION_ENABLED = false;

// Field widths at which the placeholder shortens and then drops entirely.
const FULL_LABEL_WIDTH = 380;
const SHORT_LABEL_WIDTH = 200;

type DraftState = { source: string; value: string };

function createDraftState(source: string): DraftState {
  return { source, value: source };
}

type MatrixQuestionFieldProps = {
  question: string;
};

// The question the whole matrix answers, next to its name in the toolbar.
export function MatrixQuestionField({ question }: MatrixQuestionFieldProps) {
  const [draft, setDraft] = useState(() => createDraftState(question));
  const [width, setWidth] = useState(0);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Switching matrices keeps this field mounted, so re-sync during render.
  let currentDraft = draft;
  if (currentDraft.source !== question) {
    currentDraft = createDraftState(question);
    setDraft(currentDraft);
  }

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry?.contentRect.width ?? 0);
    });
    observer.observe(field);
    return () => observer.disconnect();
  }, []);

  // 0 while unmeasured or hidden; both fall back to the widest state.
  const isMeasured = width > 0;
  const showsLabel = !isMeasured || width >= SHORT_LABEL_WIDTH;
  const showsFullLabel = !isMeasured || width >= FULL_LABEL_WIDTH;

  const field = (
    <div
      ref={fieldRef}
      // Native tooltip on the wrapper: a disabled input takes no pointer events.
      title={currentDraft.value || undefined}
      className="relative flex h-10 min-w-0 items-center"
    >
      <span className="pointer-events-none absolute top-1/2 left-2.5 flex -translate-y-1/2 items-center">
        <ComingSoonBadge />
      </span>
      <Input
        value={currentDraft.value}
        onChange={(event) =>
          setDraft({ ...currentDraft, value: event.target.value })
        }
        disabled={!QUESTION_ENABLED}
        placeholder={
          showsFullLabel
            ? "Ask one question, fill the matrix..."
            : showsLabel
              ? "Ask..."
              : ""
        }
        aria-label="Matrix question"
        className="border-primary/15 placeholder:text-muted-foreground/70 h-10 truncate rounded-full bg-transparent pr-10 pl-[6.25rem] text-sm shadow-none dark:bg-transparent"
      />
      <button
        type="button"
        disabled={!QUESTION_ENABLED}
        aria-label="Fill the matrix from this question"
        title="Fill the matrix"
        className="text-muted-foreground hover:bg-primary/15 hover:text-primary focus-visible:ring-ring/50 absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition-colors focus-visible:ring-[3px] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
      >
        <WandSparklesIcon className="size-4" aria-hidden />
      </button>
    </div>
  );

  if (QUESTION_ENABLED) return field;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{field}</TooltipTrigger>
      <TooltipContent side="top">
        Filling a whole matrix from one question is coming soon
      </TooltipContent>
    </Tooltip>
  );
}
